import { useEffect, useRef } from 'react'
import { geo } from '@carrent/platform'

import { enqueueTripLocations } from '../offline/tripTasks'

/**
 * Сбор трека активной поездки.
 *
 * Хук копит точки и отдаёт их в офлайн-очередь пачками. Прямой
 * отправки здесь нет намеренно: связь в поездке рвётся постоянно,
 * и точка, отправленная мимо очереди, теряется вместе с сетью.
 *
 * ── Первая точка после приёмки без сигнала ────────────────────────
 *
 * Если у поездки нет стартовой координаты, машину выдали там, где
 * сигнала не было, — в подземном паркинге. Тогда первая же точка
 * уезжает как `deferred_start` и дозаполняет место выдачи. Сервер
 * примет такое дозаполнение ровно один раз, так что повторить его
 * случайно нельзя.
 *
 * ── Почему пачка копится в памяти, а не в хранилище ───────────────
 *
 * Буфер живёт секунды: как только в нём что-то есть, оно уезжает
 * в очередь, а очередь уже переживает и перезагрузку, и офлайн.
 * Второе хранилище поверх неё дублировало бы её работу.
 */

/** Как часто буфер отдаётся в очередь. */
const FLUSH_MS = 60_000

function makePointId() {
  const random = Math.random().toString(36).slice(2, 10)
  return `pt-${Date.now().toString(36)}-${random}`
}

export function useTripGeo(trip) {
  const buffer = useRef([])
  // Ставим первую точку как дозаполнение старта только если приёмка
  // прошла без координаты.
  const needsDeferredStart = useRef(false)

  const tripId = trip?.id
  const active = trip?.status === 'active'
  const hasStartLocation = Boolean(trip?.start_location)

  useEffect(() => {
    if (!active || !tripId) return undefined

    needsDeferredStart.current = !hasStartLocation
    buffer.current = []

    const flush = async () => {
      if (!buffer.current.length) return
      const points = buffer.current
      buffer.current = []
      try {
        await enqueueTripLocations({ tripId, points })
      } catch {
        // Не смогли положить в очередь — вернём точки в буфер,
        // следующая попытка через минуту.
        buffer.current = points.concat(buffer.current)
      }
    }

    const stopTracker = geo.startTracker({
      onPoint: (point) => {
        const kind = needsDeferredStart.current ? 'deferred_start' : 'ping'
        needsDeferredStart.current = false

        buffer.current.push({
          client_point_id: makePointId(),
          lat: point.lat,
          lng: point.lng,
          accuracy: point.accuracy,
          source: point.source,
          kind,
          recorded_at: new Date(point.at).toISOString(),
        })
      },
    })

    const timer = setInterval(flush, FLUSH_MS)

    return () => {
      stopTracker()
      clearInterval(timer)
      // Уходя с экрана, отдаём накопленное: иначе последние точки
      // перед сдачей машины пропали бы вместе с размонтированием.
      flush()
    }
  }, [active, tripId, hasStartLocation])
}
